import { format } from 'date-fns/format';
import { parse } from 'date-fns/parse';
import zod from 'zod';

import {
  dateValueSchema,
  normalizeDateRange,
  type NormalizedDateRange,
} from '../../core/logical/date-value.js';
import { t } from '../../core/logical/type-presets.js';
import { propertyType } from '../../core/property/property-config.js';

// re-exported so consumers of `@blocksuite/data-view/property-presets` can
// decode a cell without reaching into `core/`
export { normalizeDateRange, type NormalizedDateRange };

const DATE_FMT = 'yyyy-MM-dd';
const DATE_TIME_FMT = 'yyyy-MM-dd HH:mm';
const SEPARATOR = ' - ';

export const formatDateRange = (range: NormalizedDateRange | null) => {
  if (!range) return '';
  const fmt = range.hasTime ? DATE_TIME_FMT : DATE_FMT;
  const start = format(range.start, fmt);
  // a one-day span reads as a plain date
  if (range.start === range.end) return start;
  return `${start}${SEPARATOR}${format(range.end, fmt)}`;
};

const parseOne = (value: string) => {
  const trimmed = value.trim();
  const fmt = trimmed.includes(' ') ? DATE_TIME_FMT : DATE_FMT;
  const parsed = parse(trimmed, fmt, new Date());
  const ms = +parsed;
  return Number.isNaN(ms) ? null : { ms, hasTime: fmt === DATE_TIME_FMT };
};

export const parseDateRange = (value: string): NormalizedDateRange | null => {
  const [startStr, endStr] = value.split(SEPARATOR);
  if (startStr == null) return null;
  const start = parseOne(startStr);
  if (!start) return null;
  const end = endStr != null ? parseOne(endStr) : null;
  return normalizeDateRange({
    start: start.ms,
    end: end?.ms ?? start.ms,
    hasTime: start.hasTime || !!end?.hasTime,
  });
};

export const dateRangePropertyType = propertyType('date-range');
export const dateRangePropertyModelConfig = dateRangePropertyType.modelConfig({
  name: 'Date range',
  propertyData: {
    schema: zod.object({}),
    default: () => ({}),
  },
  jsonValue: {
    // `toJson` always emits the normalised object, so a json value that is
    // *read* is always a range. This schema guards the write path
    // (`jsonValueSet` -> `fromJson`) and has to stay lenient: dragging a row
    // into a date group hands us the group's bare timestamp, and rejecting it
    // would drop the write silently.
    schema: dateValueSchema,
    isEmpty: ({ value }) => value == null,
    type: () => t.dateRange.instance(),
  },
  rawValue: {
    // deliberately the same lenient union `date` uses: a column converted
    // from `date`, or written by an older build, must still parse — a failed
    // `safeParse` makes `cellValueGet` drop the value entirely
    schema: dateValueSchema,
    default: () => null,
    toString: ({ value }) => formatDateRange(normalizeDateRange(value)),
    fromString: ({ value }) => ({ value: parseDateRange(value) }),
    toJson: ({ value }) => normalizeDateRange(value),
    fromJson: ({ value }) => normalizeDateRange(value),
  },
});
